import { z } from 'zod'

import { toneSchema } from '@/schemas/common'

/**
 * Market update — the company, sector and broker news feed.
 *
 * A feed, so it pages by cursor like Happening and Notifications rather than
 * by the numbered pager the Market tables use: it is ordered by time and grows
 * at the head, and an offset page would re-serve a story the member has
 * already read the moment a new one lands.
 */

/** The four filter chips: All news · Companies · Sectors · Brokers. */
export const newsScopeSchema = z.enum(['all', 'companies', 'sectors', 'brokers'])

/** What the story is about, which decides its badge and its leading tile. */
export const newsKindSchema = z.enum(['company', 'sector', 'broker'])

/**
 * The mock's Sort menu. mm-be orders the feed `-date, -id` and takes no order param, so only
 * `newest` is served; `oldest` is the proposed one (`asks/nw1.md`), drawn and selectable, and
 * selecting it shows the no-data state (owner ruling 1, 2026-10-28).
 */
export const newsSortSchema = z.enum(['newest', 'oldest'])

/** The Filters dialog's "Show" group. Only `everything` is served. */
export const newsShowSchema = z.enum(['everything', 'holdings', 'watchlist'])

/** The Filters dialog's "Sector" group, the mock's six. No sector filter is served yet. */
export const newsSectorSchema = z.enum([
  'commercial-banks',
  'hydropower',
  'microfinance',
  'manufacturing',
  'investment',
  'hotels',
])

/** The Filters dialog's "Source" group. No source filter is served yet. */
export const newsSourceSchema = z.enum(['sarokar', 'sansar', 'kendra', 'filing', 'notice'])

/** The chip on the right of a story: "You hold", "Applied", "Watchlist". */
export const newsBadgeSchema = z.object({
  label: z.string().min(1),
  tone: toneSchema,
})

export const newsStorySchema = z.object({
  id: z.string().min(1),
  kind: newsKindSchema,
  title: z.string().min(1),
  body: z.string().min(1),
  /** The pills under the story: symbols and sector names, already worded. */
  tags: z.array(z.string().min(1)),
  /**
   * The tag that is a listed company's symbol, when the story is about one
   * (`entity_type === 'company'`). That pill links to `/stocks/$symbol`; a
   * sector or broker tag stays plain.
   */
  symbol: z.string().min(1).optional(),
  /** Where it came from: "Company filing", "NEPSE floorsheet", "SEBON notice". */
  source: z.string().min(1),
  /**
   * Already rendered: "2h ago" for today, "Yesterday" for the day before. The
   * backend decides the cut-off, so the client never re-derives it and two
   * rows can never disagree about what "today" means.
   */
  timeLabel: z.string().min(1),
  /**
   * The wire's own `published_at`, verbatim: `YYYY-MM-DD` today, a full timestamp the day mm-be
   * adds a time. Home's "Today's news" words the age from it ("2h ago", "6d ago", then the date),
   * which `timeLabel`'s already-rendered date cannot give.
   */
  publishedAt: z.string().min(1),
  day: z.enum(['today', 'earlier']),
  /**
   * The day group the feed files the story under: "Today", "Yesterday", then the BS date. Worded
   * once in the service from `publishedAt` against Kathmandu's today, so every row of one day
   * carries the same words.
   */
  dayLabel: z.string().min(1),
  badge: newsBadgeSchema.optional(),
})

/**
 * The feed's query. The three filter groups mm-be does not serve are optional, and a caller
 * leaves each out at its served value (`everything`, none), so the default view keys the same
 * whether Home or Market update asks for it.
 */
export const newsFeedQuerySchema = z.object({
  scope: newsScopeSchema,
  query: z.string().optional(),
  /** Only posts about this company, by exact symbol: the chart rail's News, not a text search. */
  entity: z.string().min(1).optional(),
  sort: newsSortSchema,
  show: newsShowSchema.optional(),
  sectors: z.array(newsSectorSchema).optional(),
  sources: z.array(newsSourceSchema).optional(),
})

/** Shaped like `Page<T>` from `@/api/envelope`, so the swap to `getPage()` is
 *  a body change and nothing else. */
export const newsPageSchema = z.object({
  rows: z.array(newsStorySchema),
  nextCursor: z.string().optional(),
  total: z.number().int().nonnegative(),
})

export type NewsScope = z.infer<typeof newsScopeSchema>
export type NewsKind = z.infer<typeof newsKindSchema>
export type NewsSort = z.infer<typeof newsSortSchema>
export type NewsShow = z.infer<typeof newsShowSchema>
export type NewsSector = z.infer<typeof newsSectorSchema>
export type NewsSource = z.infer<typeof newsSourceSchema>
export type NewsBadge = z.infer<typeof newsBadgeSchema>
export type NewsStory = z.infer<typeof newsStorySchema>
export type NewsFeedQuery = z.infer<typeof newsFeedQuerySchema>
export type NewsPage = z.infer<typeof newsPageSchema>
